import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import {
  useCentralSeries,
  periodToRange,
  type CentralQueryResult,
  type CentralSeriesPoint,
} from '@/api/manticore';
import { useServiceCdnAnalytics } from '@/api/manticore/service-cdn';
import type { BarListItem } from '@vnetwork/ui/data';
import { formatNum, fmtAxisTime, sumSeries } from '../_shared/analytics-format';
import type { PeriodOpt } from '../_shared/AnalyticsKit';

// ---------------------------------------------------------------------------
// Scoped analytics hook — transparently delegates to service-scoped or
// org-wide endpoint based on whether a serviceId is present in the URL.
// Called inside tab sub-components (useParams pattern, no prop drilling).
// ---------------------------------------------------------------------------
export function useScopedSeries(args: Parameters<typeof useCentralSeries>[0]) {
  const { serviceId } = useParams<{ serviceId?: string }>();
  // periodToRange() reads `now`, so a fresh call every render produces new
  // from/to timestamps. useServiceCdnAnalytics keys on from/to, so an unmemoized
  // range churns the query key every render → infinite refetch loop. Memoize
  // per-period to keep the key stable.
  const { from, to, granularity } = useMemo(
    () => args.range ?? periodToRange(args.period, args.granularity),
    [args.range, args.period, args.granularity],
  );

  // Org-wide fetch — disabled when scoped.
  const orgQ = useCentralSeries({ ...args, enabled: (args.enabled ?? true) && !serviceId });

  // Service-scoped fetch — disabled when no serviceId. Forward the domain scope
  // ('all' means no narrowing) so the Service Overview domain picker filters the
  // family down to the chosen domains.
  const svcQ = useServiceCdnAnalytics(serviceId ?? '', {
    metric: args.metric,
    from,
    to,
    granularity,
    groupBy: args.groupBy,
    breakdown: args.breakdown,
    domainIds: args.domainId && args.domainId !== 'all' ? args.domainId : undefined,
  });
  // Wrap svcQ.data to match the CentralQueryResult | null | undefined shape
  // that all existing consumers expect (useCentralSeries returns exactly that).
  const svcWrapped = {
    ...svcQ,
    data: svcQ.data ?? undefined,
  } as typeof orgQ;

  return serviceId ? svcWrapped : orgQ;
}

/**
 * Refetch the analytics series queries regardless of scope. `useScopedSeries`
 * keys org-wide reads under ['cdn','central-analytics',…] but service-scoped
 * reads under ['cdn','service',<id>,'analytics',…]; the Refresh button must hit
 * both, so match by predicate instead of a single prefix.
 */
export function invalidateAnalytics(qc: QueryClient) {
  return qc.invalidateQueries({
    predicate: (q) => {
      const k = q.queryKey;
      return (
        k[0] === 'cdn' &&
        (k[1] === 'central-analytics' || (k[1] === 'service' && k[3] === 'analytics'))
      );
    },
  });
}

export const MONITORING_PERIODS: PeriodOpt[] = [
  { value: '1h', label: '1H' },
  { value: '6h', label: '6H' },
  { value: '24h', label: '24H' },
  { value: 'today', label: 'Today' },
  { value: 'last_day', label: 'Yesterday' },
];
export const SERVICE_PERIODS: PeriodOpt[] = [
  { value: '24h', label: '24H' },
  { value: '7d', label: '7D' },
  { value: '30d', label: '30D' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
];
export const LOCATION_PERIODS = SERVICE_PERIODS;

// ---- series → chart rows -------------------------------------------------
export function seriesRows(
  result: CentralQueryResult | null | undefined,
  key: string,
): Record<string, unknown>[] {
  const g = result?.granularity ?? 'day';
  return (result?.series ?? []).map((p) => ({ t: fmtAxisTime(p.timestamp, g), [key]: Number(p.value) || 0 }));
}

/** Merge two series by index into `{ t, a, b }` rows (same bucket grid). */
export function mergeRows(
  ra: CentralQueryResult | null | undefined,
  rb: CentralQueryResult | null | undefined,
  ka: string,
  kb: string,
): Record<string, unknown>[] {
  const g = (ra ?? rb)?.granularity ?? 'day';
  const a = ra?.series ?? [];
  const b = rb?.series ?? [];
  const len = Math.max(a.length, b.length);
  const rows: Record<string, unknown>[] = [];
  for (let i = 0; i < len; i++) {
    const stamp = (a[i] ?? b[i])?.timestamp ?? '';
    rows.push({ t: fmtAxisTime(stamp, g), [ka]: Number(a[i]?.value) || 0, [kb]: Number(b[i]?.value) || 0 });
  }
  return rows;
}

/**
 * Distinct line colors for per-provider breakdowns (the agg total uses the info
 * tone). Cycled if a service has more providers than colors. Shared so every
 * analytics chart paints a given provider the same way.
 */
export const PROVIDER_LINE_COLORS = [
  'var(--vn-success-solid)',
  'var(--vn-warning-solid)',
  'var(--vn-danger-solid)',
  'var(--vn-accent-solid)',
  'var(--vn-fg-muted)',
];

/**
 * Build multi-line chart rows from a grouped result: the aggregate series under
 * `totalKey` PLUS one column per provider (top-N by volume). The agg `series`
 * and `by_provider` share the same bucket grid (one query, `group_by=provider`),
 * so they merge by index. Returns the rows + the ordered provider keys so the
 * caller can declare one chart series per provider.
 */
export function providerLineRows(
  result: CentralQueryResult | null | undefined,
  totalKey: string,
  opts: { max?: number } = {},
): { rows: Record<string, unknown>[]; providers: string[] } {
  const g = result?.granularity ?? 'day';
  const ranked = Object.entries(result?.by_provider ?? {})
    .map(([name, pts]) => ({ name, pts, total: sumSeries(pts) }))
    .filter((e) => e.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, opts.max ?? 6);
  const providers = ranked.map((e) => e.name);
  const ts = result?.series ?? [];
  const len = ranked.reduce((m, e) => Math.max(m, e.pts.length), ts.length);
  const rows: Record<string, unknown>[] = [];
  for (let i = 0; i < len; i++) {
    const stamp = (ts[i] ?? ranked.find((e) => e.pts[i])?.pts[i])?.timestamp ?? '';
    const row: Record<string, unknown> = { t: fmtAxisTime(stamp, g), [totalKey]: Number(ts[i]?.value) || 0 };
    for (const e of ranked) row[e.name] = Number(e.pts[i]?.value) || 0;
    rows.push(row);
  }
  return { rows, providers };
}

/**
 * HTTP status classes for the central `Status` metric (`breakdown=code` returns
 * `by_dimension` keyed 2xx/3xx/4xx/5xx). Shared so the status-code donut (Service
 * Overview) and the status-code timeline (Monitoring) stay color-consistent —
 * `tone` maps to `toneColor()` at the call site.
 */
export const STATUS_GROUPS = [
  { key: '2xx', tone: 'success', label: '2xx Success' },
  { key: '3xx', tone: 'info', label: '3xx Redirect' },
  { key: '4xx', tone: 'warning', label: '4xx Client' },
  { key: '5xx', tone: 'danger', label: '5xx Server' },
] as const;

/**
 * Build stacked time-series rows from a `by_dimension` breakdown: one column per
 * requested key. Returns the rows plus the subset of keys that actually carried
 * data, so the caller only declares series for classes that occurred (keeps the
 * legend clean). Order follows `keys`.
 *
 * `by_dimension` is SPARSE and is NOT zero-filled per key — the store only writes
 * buckets where that dimension had traffic, so each key has its own independent
 * set of timestamps (a rare 5xx has a handful of buckets while 2xx has all of
 * them). Rows are therefore aligned on the UNION of bucket timestamps, placing
 * each key's value at its TRUE bucket (0 where absent). Merging by array index
 * would misplace a sparse class — e.g. a few 5xx buckets rendering at the start
 * of the window instead of when they actually happened.
 */
export function dimensionStackRows(
  result: CentralQueryResult | null | undefined,
  keys: readonly string[],
): { rows: Record<string, unknown>[]; present: string[] } {
  const g = result?.granularity ?? 'day';
  const dim = result?.by_dimension ?? {};
  const present = keys.filter((k) => sumSeries(dim[k]) > 0);
  // Per-key timestamp→value lookup, and the sorted union of all buckets. RFC3339
  // UTC stamps sort lexically = chronologically.
  const lut = Object.fromEntries(
    present.map((k) => [k, new Map((dim[k] ?? []).map((p) => [p.timestamp, Number(p.value) || 0]))]),
  ) as Record<string, Map<string, number>>;
  const stamps = [...new Set(present.flatMap((k) => (dim[k] ?? []).map((p) => p.timestamp)))].sort();
  const rows = stamps.map((ts) => {
    const row: Record<string, unknown> = { t: fmtAxisTime(ts, g) };
    for (const k of present) row[k] = lut[k].get(ts) ?? 0;
    return row;
  });
  return { rows, present };
}

/** A keyed map of series (`by_dimension` / `by_provider`) → ranked bars. */
export function mapToBars(
  map: Record<string, CentralSeriesPoint[]> | undefined,
  opts: { max?: number; only?: (k: string) => boolean; display?: (n: number) => string } = {},
): BarListItem[] {
  const { max = 8, only, display = formatNum } = opts;
  return Object.entries(map ?? {})
    .filter(([k]) => (only ? only(k) : true))
    .map(([label, pts]) => {
      const value = sumSeries(pts);
      return { label, value, display: display(value) } satisfies BarListItem;
    })
    .filter((b) => b.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, max);
}
